import {
  Component,
  effect,
  Inject,
  inject,
  Renderer2,
  ViewChild,
} from '@angular/core';
import {
  AtlasEventCallback,
  GlobalsStore,
} from '../../../../stores/globals.state';
import { ActiveUserStore } from '../../../../stores/user/active-user.state';
import { AtlasGridComponent } from '../../../organisms/atlas-grid/atlas-grid.component';
import { BookmarkMenuComponent } from '../../../organisms/bookmark-menu/bookmark-menu.component';
import { getState, patchState } from '@ngrx/signals';
import { SidebarModule } from 'primeng/sidebar';
import { StyleClassModule } from 'primeng/styleclass';
import { CustomGridStackWidget } from '../../../../services/grid-widget-config.service';
import { RoutingService } from '../../../../services/routing.service';
import { BookmarkFormComponent } from '../../../organisms/bookmark-form/bookmark-form.component';
import { HeaderComponent } from '../../../atoms/header/header.component';
import { GridStore } from '../../../../stores/grid/grid.state';
import { ActivatedRouteSnapshot, Router, RouterOutlet } from '@angular/router';
import { DistrictReportComponent } from '../../../organisms/district-report/district-report.component';
import { StoryGalleryComponent } from '../../../organisms/story-gallery/story-gallery.component';
import { AtlasLandingpageComponent } from '../../../organisms/atlas-landingpage/atlas-landingpage.component';
import { Commune } from '../../../../models/commune';
import { LogoComponent } from '../../../atoms/logo/logo.component';
import { DividerModule } from 'primeng/divider';
import { AtlasYearControlsComponent } from '../../../molecules/atlas-year-controls/atlas-year-controls.component';
import { ColorsetFormComponent } from '../../../organisms/colorset-form/colorset-form.component';
import { DialogModule } from 'primeng/dialog';
import { AtlasHandler } from '../../../../handlers/atlas-handler';
import { BookmarkCategoryFormComponent } from '../../../organisms/bookmark-category-form/bookmark-category-form.component';
import { InfoComponent } from '../../../organisms/grid/widgets/info/info.component';
import { Indicator } from '../../../../models/indicator';
import { AtlasLandingpageSkeletonComponent } from '../../../organisms/atlas-landingpage-skeleton/atlas-landingpage-skeleton.component';
import { AtlasGridSkeletonComponent } from '../../../organisms/atlas-grid-skeleton/atlas-grid-skeleton.component';
import { AtlasStorySkeletonComponent } from '../../../organisms/atlas-story-skeleton/atlas-story-skeleton.component';
import { Bookmark } from '../../../../models/bookmark';
import { WorkspaceFormComponent } from '../../../organisms/workspace-form/workspace-form.component';
import { DOCUMENT } from '@angular/common';
import { LegalFooterComponent } from '../../../atoms/legal-footer/legal-footer.component';

@Component({
  selector: 'app-authenticated-atlas',
  standalone: true,
  imports: [
    AtlasGridComponent,
    BookmarkMenuComponent,
    SidebarModule,
    StyleClassModule,
    BookmarkFormComponent,
    HeaderComponent,
    RouterOutlet,
    DistrictReportComponent,
    StoryGalleryComponent,
    AtlasLandingpageComponent,
    LogoComponent,
    DividerModule,
    AtlasYearControlsComponent,
    ColorsetFormComponent,
    DialogModule,
    BookmarkCategoryFormComponent,
    InfoComponent,
    AtlasLandingpageSkeletonComponent,
    AtlasGridSkeletonComponent,
    AtlasStorySkeletonComponent,
    WorkspaceFormComponent,
    LegalFooterComponent,
  ],
  templateUrl: './authenticated-atlas.component.html',
  styleUrl: './authenticated-atlas.component.scss',
})
export class AuthenticatedAtlasComponent {
  readonly globalStore = inject(GlobalsStore);
  readonly gridStore = inject(GridStore);
  readonly userStore = inject(ActiveUserStore);
  readonly atlasHandler = inject(AtlasHandler);
  private readonly router = inject(Router);

  @ViewChild(AtlasGridComponent) grid!: any;
  showLandingPage = true;
  addNewColorsetDialog = false;
  addNewBookmarkCategoryDialog = false;
  public addWorkspaceDialog = '';
  showIndicatorInfoOverlay = false;
  infoIndicator: Indicator | null = null;
  selectedWidgetId: string | null = null;
  disableControls = false;
  readonly = false;
  activeBookmark: Bookmark | null = null;
  protected storesInitialized = false;
  /** Loading skeleton matching what the initial route will show. */
  protected readonly initialSkeleton = this.detectInitialSkeleton();

  /**
   * Retrieves the currently active commune managed by the atlas handler.
   *
   * @return {Commune | null} The active commune if one is set, otherwise null.
   */
  get activeCommune(): Commune | null {
    return this.atlasHandler.activeCommune;
  }

  /**
   * Whether nothing is currently active - no topic and no bookmark - i.e. the
   * blank "Werkstatt" workspace reached via "Erstellen" on the landing page.
   *
   * @return {boolean} True while the reduced "Werkstatt" state should apply.
   */
  get isWorkshopMode(): boolean {
    return this.gridStore.isWorkshopMode();
  }

  /**
   * Determines what the initial route leads to - a report (story route or
   * `?story=`), a view (bookmark route or `?topic=`) or the landing page - to
   * pick the matching loading skeleton before the stores are initialized.
   *
   * @return {'story' | 'view' | 'landing'} The skeleton to show while loading.
   */
  private detectInitialSkeleton(): 'story' | 'view' | 'landing' {
    let route: ActivatedRouteSnapshot = this.router.routerState.snapshot.root;
    while (route.firstChild) route = route.firstChild;
    if (
      route.data['entityType'] === 'Story' ||
      route.queryParamMap.get('story')
    ) {
      return 'story';
    }
    if (
      route.data['entityType'] === 'Bookmark' ||
      route.queryParamMap.get('topic')
    ) {
      return 'view';
    }
    return 'landing';
  }

  constructor(
    private routingService: RoutingService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document
  ) {
    effect(() => {
      if (!this.storesInitialized && !this.atlasHandler.areStoresInitialized())
        return;
      this.storesInitialized = true;
      queueMicrotask(() => this.atlasHandler.performInitialization());

      const globalState = getState(this.globalStore);
      const gridState = getState(this.gridStore);

      this.infoIndicator = globalState.selectedIndicator || null;
      this.selectedWidgetId = globalState.selectedWidget || null;
      this.showIndicatorInfoOverlay = !!(
        this.selectedWidgetId || this.infoIndicator
      );

      this.activeBookmark = gridState.activeBookmark || null;

      this.disableControls =
        !!gridState.activeBookmark?.disableControls &&
        gridState.readonly === true;

      if (globalState.atlasEventCallback) {
        this.globalEventCallback(globalState.atlasEventCallback);
      }

      if (
        globalState.activeTopic ||
        globalState.activeReport ||
        globalState.activeStory ||
        gridState.activeBookmark
      ) {
        this.showLandingPage = false;
      }
      this.toggleBodyScroll(this.showLandingPage);
    });
  }

  /**
   * Handles the logic to reset the selected indicator and widget information
   * when the indicator information overlay is closed.
   *
   * @return {void} Does not return a value. Executes the state update process.
   */
  onIndicatorInfoOverlayClose() {
    patchState(this.globalStore, {
      selectedIndicator: null,
      selectedWidget: null,
    });
  }

  /**
   * Toggles the ability to scroll the body element by adding or removing a CSS class.
   *
   * @param {boolean} disable - If `true`, disables scrolling by adding a 'no-scroll' class to the body element.
   *                            If `false`, re-enables scrolling by removing the class.
   * @return {void} Does not return a value.
   */
  private toggleBodyScroll(disable: boolean): void {
    if (disable) {
      this.renderer.addClass(this.document.body, 'overflow-hidden');
    } else {
      this.renderer.removeClass(this.document.body, 'overflow-hidden');
    }
  }

  onLoadLayout($event: CustomGridStackWidget[]) {}

  /**
   * catch and proceed global events
   *
   * @param atlasEventCallback
   * @private
   */
  private globalEventCallback(atlasEventCallback: AtlasEventCallback) {
    switch (atlasEventCallback.key) {
      case 'openLandingPage':
        setTimeout(() => {
          this.gridStore.unsetActiveBookmark();
          this.routingService.redirectToRoute([
            this.routingService.getRootByContext(
              this.userStore.context(),
              this.activeCommune?.slug || this.activeCommune?._id || ''
            ),
          ]);
          this.showLandingPage = true;
        });

        break;
      case 'closeLandingPage':
        this.showLandingPage = false;
        break;
      case 'addColorsetDialog':
        this.addNewColorsetDialog = true;
        break;
      case 'addWorkspaceDialog':
        this.addWorkspaceDialog = atlasEventCallback.options?.key || '';
        break;
      case 'addBookmarkCategoryDialog':
        this.addNewBookmarkCategoryDialog = true;
        break;
    }
  }

  /**
   * Closes the bookmark category dialog by setting the dialog's visibility state to false.
   *
   * @return {void} Does not return any value.
   */
  closeBookmarkCategoryDialog(): void {
    this.addNewBookmarkCategoryDialog = false;
  }
}
